import { userAvatarBucketName } from '@/config/env';
import { useUIStore } from '@/config/stores/ui-store';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { GradientAvatar } from '@/shared/components';
import { Badge, useTheme } from '@mui/material';
import { memo, useMemo } from 'react';
import type { Member } from '../../models/member';

interface ConversationMemberAvatarProps {
  member: Member;
}

const BADGE_SIZE = 8;

const ConversationMemberAvatarComponent = ({ member }: ConversationMemberAvatarProps) => {
  const theme = useTheme();

  // Subscribe to specific friend's online status - only re-renders when THIS user's status changes
  const onlineStatus = useUIStore((state) => {
    const friend = state.friends.find((f) => f.userId === member.userId);
    return friend?.isOnline ?? false;
  });
  const user = useAuthStore((state) => state.user);

  const isCurrentUser = useMemo(() => user?.user_id === member.userId, [user?.user_id, member.userId]);
  // Memoize badge styles to avoid recreating the object on every render
  const badgeStyles = useMemo(
    () => ({
      '& .MuiBadge-badge': {
        backgroundColor: onlineStatus || isCurrentUser ? theme.vars.palette.success.main : theme.vars.palette.grey.A400,
        color: onlineStatus || isCurrentUser ? theme.vars.palette.success.main : theme.vars.palette.grey.A400,
        boxShadow: `0 0 0 2px ${theme.vars.palette.common.white}`,
        width: BADGE_SIZE,
        height: BADGE_SIZE,
        borderRadius: '50%'
      }
    }),
    [onlineStatus, isCurrentUser, theme]
  );

  return (
    <Badge
      overlap='circular'
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      variant='dot'
      sx={badgeStyles}
    >
      <GradientAvatar
        peerId={member.userId}
        className='!size-8'
        src={member.user?.profile?.avatarRef || undefined}
        bucketName={userAvatarBucketName}
        name={member.user?.fullName}
      />
    </Badge>
  );
};

// Memoize the component to prevent unnecessary re-renders when props haven't changed
export const ConversationMemberAvatar = memo(ConversationMemberAvatarComponent);
